import type { UserPermissionsManagerAPIInterfaceInternal } from "@dealer-center/ddc-pages-user-permissions-manager-interface";
import type { TypeSafeTableColumns } from "@dealer-center/core-components";
import {
  Alert,
  Box,
  Button,
  PlusCircleIcon,
  Table,
  TrashIcon,
  Typography
} from "@dealer-center/core-components";
import { LoadingIndicator } from "@dealer-center/core-custom-components";
import { Outlet, useParams } from "react-router-dom";
import { usePermissionsForUserGet } from "@dealer-center/ddc-pages-user-permissions-manager-fetch";
import styled from "styled-components";
import { usePermissionGroupModals } from "./modals";
import { TagManager } from "@dealer-center/core-third-party-gtm";

const StyledAlert = styled(Alert)`
  margin-bottom: 1rem;
`;

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
  width: 100%;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const StyledTable = styled(Table.Table)`
  width: 100%;
`;

const checkPermissions = data => {
  if (data === '{"message":"Not Found"}') {
    return (
      <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="info">
        User Not Found
      </StyledAlert>
    );
  } else if (data === "[null]") {
    return (
      <StyledAlert role="alert" sx={{ marginBottom: "16px" }} type="info">
        User Has No Permissions
      </StyledAlert>
    );
  } else {
    return undefined;
  }
};

const transformData = jsonData => {
  const data = JSON.parse(jsonData);
  return data.map((item, index) => ({
    key: index,
    permission: item
  }));
};

export function PagesUserPermissionsManagerUiResults() {
  const { userId } = useParams();
  const { data, error, isLoading } = usePermissionsForUserGet(userId);
  const userNotFoundOrNoPermissions = checkPermissions(data);
  const permissionGroupModal = usePermissionGroupModals();

  const buildColumns = (
    userId
  ): TypeSafeTableColumns<UserPermissionsManagerAPIInterfaceInternal> => [
    {
      dataIndex: "permission",
      title: "Permission Group"
    },
    {
      dataIndex: "remove",
      title: "Remove",
      columnSortable: false,
      render: (text, record) => (
        <Button
          analyticsConfiguration={{
            eventName: "Remove User from Permission Group"
          }}
          buttonStyle="aux"
          data-testid={`remove-${userId}-from-group-${record.permission}`}
          onClick={() => {
            TagManager.event({
              eventName: "Remove User from Permission Group",
              eventProperties: {
                component: "dashboard",
                actionValue: record.permission
              }
            });
            permissionGroupModal.showRemoveUserFromGroupModal({
              userId,
              permissionGroup: record.permission
            });
          }}
        >
          <TrashIcon title="Remove User from Permission Group" />
        </Button>
      )
    }
  ];
  if (error)
    return (
      <Alert role="alert" sx={{ marginBottom: "16px" }} type="error">
        {error.message}
      </Alert>
    );

  if (isLoading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <LoadingIndicator
          actionName="Loading PagesUserPermissionsManagerUi"
          text="Loading"
        />
      </Box>
    );

  if (userId) {
    const columns = buildColumns(userId);
    const transformedData = userNotFoundOrNoPermissions
      ? null
      : transformData(data);
    return (
      <div>
        <Box sx={{ pt: 4 }}>
          <StyledHeader>
            <Typography variant={"h2"}>Permission Groups</Typography>
            <Button
              analyticsConfiguration={{
                eventName: "Add User to Permission Groups"
              }}
              buttonStyle="primary"
              data-testid={`remove-to-permission-groups`}
              id="AddPermission"
              size="small"
              startIcon={<PlusCircleIcon title="Add Permission Group" />}
              onClick={() => {
                TagManager.event({
                  eventName: "Add User to Permission Groups",
                  eventProperties: {
                    component: "dashboard"
                  }
                });
                permissionGroupModal.showAddUserToGroupModal();
              }}
            >
              Add
            </Button>
          </StyledHeader>
          {userNotFoundOrNoPermissions ? (
            userNotFoundOrNoPermissions
          ) : (
            <div>
              <StyledTable
                displayFilter
                sortableColumns
                columns={columns}
                data={transformedData || []}
                emptyText="No Permission Groups Found"
                enablePagination={false}
                id="TableRowSelection"
                scrollX={500}
                tableLayout="auto"
              />
              <Outlet />
            </div>
          )}
        </Box>
        {permissionGroupModal.modal}
      </div>
    );
  }
  return <div />;
}
